import CreateDashboardModal from '@/components/ReportDashboard/Editor/CreateDashboardModal'
import DashboardEditor from '@/components/ReportDashboard/Editor/DashboardEditor'
import DashboardManagerTopNav from '@/components/ReportDashboard/Editor/DashboardManagerTopNav'
import { Toaster } from '@/components/Shared/Toast'
import RADashboardThemeContextProvider from '@/contexts/RADashboardThemeContext'
import { RADashboardLayout } from '@/interfaces/raDashboard.interface'
import { ThemeProvider, createTheme as createMuiTheme } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useSchemaVersionGate } from '@/hooks/useSchemaVersionGate'
import { createTheme } from '../../constants/risk-dashboard-colors'
import { Dashboard, dashboardService } from '../../services/report-dashboard/dashboard.service'
import { getUserPreference, putUserPreference } from '../../services/userPreference.service'

const DL_MODE_CONTEXT = 'reportDashboard.DLMode'
const MANAGE_PREFS_CONTEXT = 'reportDashboard.manageDashboards'

interface DLModePreference {
  isDarkMode: boolean
}

type DashboardSortConfig = { key: keyof Dashboard; direction: 'asc' | 'desc' }

interface ManageDashboardsPreference {
  showPublicDashboards: boolean
  sortConfig: DashboardSortConfig | null
}

const DEFAULT_MANAGE_PREFS: ManageDashboardsPreference = {
  showPublicDashboards: true,
  sortConfig: null,
}

const ManageDashboardsPage: React.FC = () => {
  const navigate = useNavigate()
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false)
  const [prefLoaded, setPrefLoaded] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showPublicDashboards, setShowPublicDashboards] = useState<boolean>(true)
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false)
  const [sortConfig, setSortConfig] = useState<DashboardSortConfig | null>(null)
  const [dashboardContexts, setDashboardContexts] = useState<{ [key: string]: string }>({})
  const [selectedDashboardDetails, setSelectedDashboardDetails] = useState<Dashboard | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [isAdminAuthorized, setIsAdminAuthorized] = useState<boolean>(false)
  const [isAdminMode, setIsAdminMode] = useState<boolean>(false)
  const hasLoaded = useRef(false)
  const hasCheckedAuth = useRef(false)
  const schemaVersionGate = useSchemaVersionGate()
  // Guards against two overlapping loadDashboards calls (e.g. rapidly
  // double-toggling Admin Mode) resolving out of order and letting a stale
  // response overwrite a newer one's state.
  const loadDashboardsSeqRef = useRef(0)

  // Source of truth for the persisted Manage Dashboards prefs. Keeping it in a
  // ref lets each save specify only the field it changed and merge against the
  // latest values, so one setting's save never clobbers another's.
  const managePrefsRef = useRef<ManageDashboardsPreference>(DEFAULT_MANAGE_PREFS)

  const saveManagePrefs = (patch: Partial<ManageDashboardsPreference>) => {
    const next = { ...managePrefsRef.current, ...patch }
    managePrefsRef.current = next
    putUserPreference<ManageDashboardsPreference>(MANAGE_PREFS_CONTEXT, next)
  }

  const theme = createTheme(isDarkMode)
  const muiTheme = useMemo(
    () => createMuiTheme({
      typography: { fontFamily: theme.fontFamily },
      palette: {
        mode: isDarkMode ? 'dark' : 'light',
        primary: {
          main: '#0a5eb2', //--cwan-palette-primary-main
          dark: '#0a5eb2', //--cwan-palette-primary-main
        },
        // cwan grey nav hover/selected (was MUI default white in dark mode)
        action: {
          hover: theme.buttonBackground,
        },
        text: {
          primary: theme.textPrimary,
        }
      }
    }),
    [isDarkMode],
  )

  // Authorization check — must succeed before loading the page
  useEffect(() => {
    if (hasCheckedAuth.current) return
    hasCheckedAuth.current = true
    dashboardService
      .canManageDashboard()
      .then((isAuthorized) => setAuthorized(isAuthorized))
      .catch(() => setAuthorized(false))
    dashboardService
      .canAdminManageDashboard()
      .then((isAuthorized) => setIsAdminAuthorized(isAuthorized))
      .catch(() => setIsAdminAuthorized(false))
  }, [])

  // Load Manage Dashboards preferences (dark mode + the public-dashboards
  // filter) from the backend on mount. Rendering is gated on prefLoaded, so the
  // restored values apply before the first paint (no flicker).
  useEffect(() => {
    Promise.all([
      getUserPreference<DLModePreference>(DL_MODE_CONTEXT),
      getUserPreference<ManageDashboardsPreference>(MANAGE_PREFS_CONTEXT),
    ])
      .then(([dlPref, managePref]) => {
        if (dlPref?.isDarkMode !== undefined) {
          setIsDarkMode(dlPref.isDarkMode)
        }
        // Seed the ref with the restored prefs (falling back to defaults for any
        // missing fields) so later merged saves start from the persisted state.
        const restored: ManageDashboardsPreference = {
          ...DEFAULT_MANAGE_PREFS,
          ...(managePref ?? {}),
        }
        managePrefsRef.current = restored
        setShowPublicDashboards(restored.showPublicDashboards)
        setSortConfig(restored.sortConfig)
      })
      .finally(() => {
        setPrefLoaded(true)
      })
  }, [])

  // Load dashboards from API when component mounts
  useEffect(() => {
    if (authorized !== true) return
    if (hasLoaded.current) return
    hasLoaded.current = true

    loadDashboards()
  }, [authorized]);

  // adminModeOverride lets the Admin Mode toggle request the right list
  // immediately, since setIsAdminMode's update isn't visible in this closure
  // until the next render.
  const loadDashboards = async (silent = false, adminModeOverride?: boolean) => {
    const useAdminMode = adminModeOverride ?? isAdminMode
    const seq = ++loadDashboardsSeqRef.current
    try {
      if (!silent) {
        setSelectedDashboardDetails(null)
        setLoading(true)
      }
      const dashboardList = useAdminMode
        ? await dashboardService.getAllDashboardsMetadata('RADashboard')
        : await dashboardService.getDashboards('RADashboard')

      // A newer call has started since this one began (e.g. the Admin Mode
      // toggle was clicked again before this request finished) — drop this
      // now-stale response instead of letting it clobber the newer state.
      if (seq !== loadDashboardsSeqRef.current) return

      setDashboards(dashboardList)

      const contexts: { [key: string]: string } = {}
      dashboardList.forEach(dashboard => {
        contexts[dashboard.dashboardName] = dashboard.context
      })
      setDashboardContexts(contexts)
    } catch (error) {
      if (seq !== loadDashboardsSeqRef.current) return
      console.error('Failed to load dashboards:', error)
      toast.error('Failed to load dashboards. Please try again later.')
    } finally {
      if (seq === loadDashboardsSeqRef.current && !silent) setLoading(false)
    }
  }

  const handleSort = (key: keyof Dashboard) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    const nextSort: DashboardSortConfig = { key, direction }
    setSortConfig(nextSort)
    saveManagePrefs({ sortConfig: nextSort })
  }

  const sortedAndFilteredDashboards = React.useMemo(() => {
    // Admin Mode shows every dashboard regardless of public/private status, so
    // the public-dashboards filter is bypassed entirely.
    let sortableItems = [...dashboards].filter(dashboard => {
      if (!isAdminMode && !showPublicDashboards && !dashboard.isPrivate && !dashboard.currentUserOwned) return false
      return (
        dashboard.dashboardName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dashboard.dashboardDescription.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })

    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key]
        const bValue = b[sortConfig.key]
        
        if (aValue === null || aValue === undefined) return 1
        if (bValue === null || bValue === undefined) return -1
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }
    return sortableItems
  }, [dashboards, searchTerm, sortConfig, showPublicDashboards, isAdminMode])

  // Handle dashboard click - call appropriate API based on privacy
  const handleDashboardClick = async (dashboard: Dashboard) => {
    // The admin metadata listing doesn't include jsonValue, so fetch full
    // details (including jsonValue, needed for "Save As") via the admin-only
    // endpoint, which works regardless of who owns the dashboard.
    if (isAdminMode) {
      try {
        const dashboardDetails = await dashboardService.getDashboardAdmin(dashboard.dashboardName, dashboard.context, dashboard.userId)
        setSelectedDashboardDetails(dashboardDetails)
      } catch (error) {
        console.error('Failed to load dashboard (admin):', error)
        toast.error('Failed to load dashboard. Please try again later.')
      }
      return
    }
    try {
      const context = dashboardContexts[dashboard.dashboardName];
      const dashboardDetails = await dashboardService.getDashboard(dashboard.dashboardName, context, dashboard.userId, dashboard.currentUserOwned);

      // Show dashboard canvas below the list
      setSelectedDashboardDetails(dashboardDetails);
    } catch (error) {
      console.error('Failed to load dashboard:', error)
      toast.error('Failed to load dashboard. Please try again later.')
    }
  }

  const handleCreateDashboard = async (newDashboard: Dashboard) => {
    try {
      await dashboardService.putDashboard(newDashboard)
      // Explicitly pass the current mode rather than relying on loadDashboards'
      // default, so the right list (admin vs. own-and-public) always reloads.
      await loadDashboards(true, isAdminMode)
      const details = await dashboardService.getDashboard(newDashboard.dashboardName, newDashboard.context, newDashboard.userId, true)
      setSelectedDashboardDetails(details)
    } catch (error) {
      console.error('Failed to create dashboard:', error)
      toast.error('Failed to create dashboard. Please try again later.')
    }
  }

  const handleEditDashboardLayout = async (json: RADashboardLayout, description: string, isPrivate: boolean) => {
    const editedDashboard = { ...selectedDashboardDetails!, jsonValue: json, dashboardDescription: description, isPrivate }
    try {
      await dashboardService.putDashboard(editedDashboard)
      await loadDashboards(true, isAdminMode)
      setSelectedDashboardDetails(editedDashboard)
    } catch (error) {
      console.error('Failed to save dashboard:', error)
      toast.error('Failed to save dashboard. Please try again later.')
    }
  }

  const handleSaveAsDashboard = async (json: RADashboardLayout, newName: string, description: string, isPrivate: boolean) => {
    const copy: Dashboard = {
      ...selectedDashboardDetails!,
      // Don't carry over the source dashboard's owner — in Admin Mode that's
      // whoever the original dashboard belonged to, not the person doing the
      // Save As, and sending it through would let the backend upsert onto
      // that other user's dashboard if a name collides. Leave it empty, same
      // as CreateDashboardModal does for a brand-new dashboard, so the
      // backend attributes the copy to the caller actually making this request.
      userId: '',
      userName: '',
      currentUserOwned: true,
      dashboardName: newName,
      dashboardDescription: description,
      isPrivate,
      jsonValue: json,
    }
    try {
      await dashboardService.putDashboard(copy)
      await loadDashboards(true, isAdminMode)
      const details = await dashboardService.getDashboard(copy.dashboardName, copy.context, copy.userId, true)
      setSelectedDashboardDetails(details)
    } catch (error) {
      console.error('Failed to save dashboard:', error)
      toast.error('Failed to save dashboard. Please try again later.')
    }
  }

  const handleDeleteDashboard = async () => {
    if (!selectedDashboardDetails) return
    try {
      const deletedName = selectedDashboardDetails.dashboardDisplayName ?? selectedDashboardDetails.dashboardName
      if (isAdminMode) {
        await dashboardService.adminDeleteDashboard(selectedDashboardDetails.dashboardName, selectedDashboardDetails.context, selectedDashboardDetails.userId)
      } else {
        await dashboardService.deleteDashboard(selectedDashboardDetails.dashboardName, selectedDashboardDetails.context)
      }
      setDashboards(prev => prev.filter(d => !(d.dashboardName === selectedDashboardDetails.dashboardName && d.userId === selectedDashboardDetails.userId)))
      setConfirmDeleteOpen(false)
      setSelectedDashboardDetails(null)
      toast.success(`"${deletedName}" was deleted successfully.`)
    } catch {
      toast.error('Failed to delete dashboard. Please try again later.')
    }
  }

  // Rendered once, at a stable position outside the branches below, so a
  // toast fired right before a branch transition (e.g. loadDashboards'
  // toast.error immediately followed by setLoading(false)) isn't lost when
  // that branch's own ToastContainer would otherwise unmount before it can
  // ever show it.
  let content: React.ReactNode

  if (
    authorized === false ||
    (schemaVersionGate.loaded && !schemaVersionGate.isFactorAnalyticsEnabled)
  ) {
    content = (
      <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: theme.background,
          color: theme.textPrimary,
          padding: '24px',
          textAlign: 'center',
        }}>
          There is a policy or version mismatch, this page is not viewable. Please contact support.
        </div>
      </RADashboardThemeContextProvider>
    )
  } else if (
    authorized === null ||
    !schemaVersionGate.loaded ||
    !prefLoaded ||
    loading
  ) {
    content = (
      <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: theme.background,
          color: theme.textPrimary,
        }}>
          {prefLoaded && authorized === true ? 'Loading dashboards...' : ''}
        </div>
      </RADashboardThemeContextProvider>
    )
  } else {
    content = (
    <ThemeProvider theme={muiTheme}>
    <RADashboardThemeContextProvider isDarkMode={isDarkMode} theme={theme}>
    <div style={{
      background: theme.background,
      color: theme.textPrimary,
      minHeight: '100vh',
      overflowY: 'auto'
    }}>

      <DashboardManagerTopNav
        isDarkMode={isDarkMode}
        onDarkModeToggle={(isDark) => {
            setIsDarkMode(isDark)
            putUserPreference<DLModePreference>(DL_MODE_CONTEXT, {
              isDarkMode: isDark,
            })
          }}
        />

      <div style={{
        padding: '0px 24px'
      }}>
      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <input
          type="text"
          placeholder="Search dashboards..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            maxWidth: '400px',
            padding: '10px 16px',
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: theme.cardBackground,
            color: theme.textPrimary
          }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: isAdminMode ? theme.border : theme.textSecondary, cursor: isAdminMode ? 'default' : 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={showPublicDashboards || isAdminMode}
            disabled={isAdminMode}
            onChange={(e) => {
              const next = e.target.checked
              setShowPublicDashboards(next)
              saveManagePrefs({ showPublicDashboards: next })
            }}
            style={{ width: '16px', height: '16px', cursor: isAdminMode ? 'default' : 'pointer' }}
          />
          Show Public Dashboards
        </label>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
          {isAdminAuthorized && schemaVersionGate.isAdminModeEnabled && (
            // No loading/disabled styling needed here: this button (and the
            // rest of this content) only renders once loading is false — the
            // branch above swaps to a full-screen "Loading dashboards..."
            // message for the whole page while a reload (including this
            // toggle's own) is in flight, so a second click can't happen.
            <button
              onClick={() => {
                const next = !isAdminMode
                setIsAdminMode(next)
                loadDashboards(false, next)
              }}
              style={{
                padding: '10px 20px',
                border: `1px solid ${isAdminMode ? '#d32f2f' : theme.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: isAdminMode ? '#d32f2f' : theme.cardBackground,
                color: isAdminMode ? 'white' : theme.textPrimary,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {isAdminMode ? 'Admin Mode: On' : 'Admin Mode'}
            </button>
          )}

          <button
            onClick={() => setShowCreateDialog(true)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              backgroundColor: theme.runButtonBackground,
              color: theme.runButtonColor,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Create New Dashboard
          </button>
        </div>
      </div>

      {/* Dashboard Table */}
      <div style={{
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: isDarkMode 
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        maxHeight: '500px',
        overflowY: 'auto'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px'
        }}>
          <thead>
            <tr style={{
              backgroundColor: isDarkMode ? '#1a1a1a' : '#f8fafc',
              borderBottom: `2px solid ${theme.border}`,
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <th 
                onClick={() => handleSort('dashboardName')}
                style={{
                  padding: '18px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: sortConfig?.key === 'dashboardName' 
                    ? (isDarkMode ? '#2a2a2a' : '#e2e8f0')
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (sortConfig?.key !== 'dashboardName') {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#252525' : '#f1f5f9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (sortConfig?.key !== 'dashboardName') {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Dashboard Name
                  <span style={{ 
                    fontSize: '14px', 
                    color: sortConfig?.key === 'dashboardName' 
                      ? (isDarkMode ? '#60a5fa' : '#3b82f6') 
                      : theme.textSecondary,
                    opacity: sortConfig?.key === 'dashboardName' ? 1 : 0.6,
                    transition: 'all 0.2s ease'
                  }}>
                    {sortConfig?.key === 'dashboardName' 
                      ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                      : '⇅'
                    }
                  </span>
                </div>
              </th>
              <th 
                onClick={() => handleSort('dashboardDescription')}
                style={{
                  padding: '18px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: sortConfig?.key === 'dashboardDescription' 
                    ? (isDarkMode ? '#2a2a2a' : '#e2e8f0')
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (sortConfig?.key !== 'dashboardDescription') {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#252525' : '#f1f5f9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (sortConfig?.key !== 'dashboardDescription') {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Description
                  <span style={{ 
                    fontSize: '14px', 
                    color: sortConfig?.key === 'dashboardDescription' 
                      ? (isDarkMode ? '#60a5fa' : '#3b82f6') 
                      : theme.textSecondary,
                    opacity: sortConfig?.key === 'dashboardDescription' ? 1 : 0.6,
                    transition: 'all 0.2s ease'
                  }}>
                    {sortConfig?.key === 'dashboardDescription' 
                      ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                      : '⇅'
                    }
                  </span>
                </div>
              </th>
              <th
                onClick={() => handleSort('dashboardType')}
                style={{
                  padding: '18px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: sortConfig?.key === 'dashboardType'
                    ? (isDarkMode ? '#2a2a2a' : '#e2e8f0')
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (sortConfig?.key !== 'dashboardType') {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#252525' : '#f1f5f9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (sortConfig?.key !== 'dashboardType') {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Type
                  <span style={{
                    fontSize: '14px',
                    color: sortConfig?.key === 'dashboardType'
                      ? (isDarkMode ? '#60a5fa' : '#3b82f6')
                      : theme.textSecondary,
                    opacity: sortConfig?.key === 'dashboardType' ? 1 : 0.6,
                    transition: 'all 0.2s ease'
                  }}>
                    {sortConfig?.key === 'dashboardType'
                      ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                      : '⇅'
                    }
                  </span>
                </div>
              </th>
              <th
                onClick={() => handleSort('userName')}
                style={{
                  padding: '18px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: sortConfig?.key === 'userName' 
                    ? (isDarkMode ? '#2a2a2a' : '#e2e8f0')
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (sortConfig?.key !== 'userName') {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#252525' : '#f1f5f9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (sortConfig?.key !== 'userName') {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Created By
                  <span style={{ 
                    fontSize: '14px', 
                    color: sortConfig?.key === 'userName' 
                      ? (isDarkMode ? '#60a5fa' : '#3b82f6') 
                      : theme.textSecondary,
                    opacity: sortConfig?.key === 'userName' ? 1 : 0.6,
                    transition: 'all 0.2s ease'
                  }}>
                    {sortConfig?.key === 'userName' 
                      ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                      : '⇅'
                    }
                  </span>
                </div>
              </th>
              <th 
                onClick={() => handleSort('dateCreated')}
                style={{
                  padding: '18px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: sortConfig?.key === 'dateCreated' 
                    ? (isDarkMode ? '#2a2a2a' : '#e2e8f0')
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (sortConfig?.key !== 'dateCreated') {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#252525' : '#f1f5f9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (sortConfig?.key !== 'dateCreated') {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Created Date
                  <span style={{ 
                    fontSize: '14px', 
                    color: sortConfig?.key === 'dateCreated' 
                      ? (isDarkMode ? '#60a5fa' : '#3b82f6') 
                      : theme.textSecondary,
                    opacity: sortConfig?.key === 'dateCreated' ? 1 : 0.6,
                    transition: 'all 0.2s ease'
                  }}>
                    {sortConfig?.key === 'dateCreated' 
                      ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                      : '⇅'
                    }
                  </span>
                </div>
              </th>
              <th 
                onClick={() => handleSort('dateModified')}
                style={{
                  padding: '18px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: theme.textPrimary,
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: sortConfig?.key === 'dateModified' 
                    ? (isDarkMode ? '#2a2a2a' : '#e2e8f0')
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (sortConfig?.key !== 'dateModified') {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#252525' : '#f1f5f9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (sortConfig?.key !== 'dateModified') {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Modified Date
                  <span style={{ 
                    fontSize: '14px', 
                    color: sortConfig?.key === 'dateModified' 
                      ? (isDarkMode ? '#60a5fa' : '#3b82f6') 
                      : theme.textSecondary,
                    opacity: sortConfig?.key === 'dateModified' ? 1 : 0.6,
                    transition: 'all 0.2s ease'
                  }}>
                    {sortConfig?.key === 'dateModified' 
                      ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                      : '⇅'
                    }
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredDashboards.map((dashboard) => {
              const isSelected =
                selectedDashboardDetails?.dashboardName === dashboard.dashboardName &&
                selectedDashboardDetails?.userId === dashboard.userId
              return (
              <tr
                key={`${dashboard.userId}-${dashboard.dashboardName}`}
                style={{
                  borderBottom: `1px solid ${theme.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: isSelected
                    ? (isDarkMode ? '#1e3a5f' : '#e8f0fe')
                    : 'transparent',
                  borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                }}
                onClick={() => handleDashboardClick(dashboard)}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = isDarkMode ? '#252525' : '#f8fafc'
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <td style={{
                  padding: '18px 16px',
                  color: theme.textPrimary,
                  fontWeight: '500',
                  verticalAlign: 'middle'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {dashboard.dashboardDisplayName ?? dashboard.dashboardName}
                    {dashboard.isLocked && (
                      <span style={{
                        padding: '3px 8px',
                        fontSize: '12px',
                        fontWeight: '700',
                        backgroundColor: isDarkMode ? '#1e40af' : '#2563eb',
                        color: 'white',
                        borderRadius: '10px',
                        letterSpacing: '0.5px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                      }}>
                        DEFAULT
                      </span>
                    )}
                    {!dashboard.isPrivate && (
                      <span style={{
                        padding: '3px 8px',
                        fontSize: '12px',
                        fontWeight: '700',
                        backgroundColor: isDarkMode ? '#059669' : '#10b981',
                        color: 'white',
                        borderRadius: '10px',
                        letterSpacing: '0.5px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                      }}>
                        PUBLIC
                      </span>
                    )}
                  </div>
                </td>
                <td style={{
                  padding: '18px 16px',
                  color: theme.textSecondary,
                  verticalAlign: 'middle',
                  maxWidth: '300px'
                }}>
                  {dashboard.dashboardDescription}
                </td>
                <td style={{
                  padding: '18px 16px',
                  color: theme.textSecondary,
                  verticalAlign: 'middle'
                }}>
                  {dashboard.dashboardType}
                </td>
                <td style={{
                  padding: '18px 16px',
                  color: theme.textSecondary,
                  verticalAlign: 'middle'
                }}>
                  {dashboard.userName}
                </td>
                <td style={{
                  padding: '18px 16px',
                  color: theme.textSecondary,
                  verticalAlign: 'middle'
                }}>
                  {new Date(dashboard.dateCreated).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
                <td style={{
                  padding: '18px 16px',
                  color: theme.textSecondary,
                  verticalAlign: 'middle'
                }}>
                  {new Date(dashboard.dateModified).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {/* No Dashboards Found */}
      {sortedAndFilteredDashboards.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '80px 20px',
          color: theme.textSecondary,
          backgroundColor: theme.cardBackground,
          borderRadius: '8px',
          border: `2px dashed ${theme.border}`,
          margin: '20px 0'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 20px',
            backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px'
          }}>
            📊
          </div>
          <h3 style={{ 
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: theme.textPrimary
          }}>
            No dashboards found
          </h3>
          <p style={{ 
            margin: 0,
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            {searchTerm ? 'Try adjusting your search terms or filters' : 'Create your first dashboard to get started'}
          </p>
        </div>
      )}

      {/* Dashboard Canvas */}
      {selectedDashboardDetails && ( 
        <DashboardEditor
          dashboard={selectedDashboardDetails}
          isAdminMode={isAdminMode}
          onClose={() => setSelectedDashboardDetails(null)}
          onSaveDashboard={handleEditDashboardLayout}
          onSaveAs={handleSaveAsDashboard}
          existingDashboardNames={dashboards.filter((d) => d.currentUserOwned).map((d) => d.dashboardName)}
          onDelete={() => setConfirmDeleteOpen(true)}
          />
      )}

    </div>

      {showCreateDialog && (
        <CreateDashboardModal
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onCreate={handleCreateDashboard}
          existingNames={dashboards.filter((d) => d.currentUserOwned).map((d) => d.dashboardName)}
        />
      )}

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: theme.cardBackground,
            color: theme.textPrimary,
            border: `1px solid ${theme.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: theme.textPrimary }}>Delete Dashboard</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.textSecondary }}>
            Are you sure you want to delete &ldquo;{selectedDashboardDetails?.dashboardDisplayName ?? selectedDashboardDetails?.dashboardName}&rdquo;?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} variant='outlined' sx={{ color: theme.textSecondary, borderColor: theme.border }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteDashboard} variant='contained' sx={{ backgroundColor: '#d32f2f', color: 'white', '&:hover': { backgroundColor: '#b71c1c' } }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    </RADashboardThemeContextProvider>
    </ThemeProvider>
    )
  }

  return (
    <>
      {content}
      <Toaster />
    </>
  )
}

export default ManageDashboardsPage
